import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { Chip, IconButton, Skeleton, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import productsApi from '../../../api/productsApi';
import { getErrorMessage } from '../../../utils/errorHandler';

// Unidades fisicas (numeros de serie) de un producto ya existente: se agregan una por una
// al recibir stock y se listan aqui. Al venderse, el backend le asigna sola la unidad mas
// antigua disponible a la orden (ver OrderController::assignSerials), por eso una serie
// vendida se muestra pero ya no se puede borrar: es el registro de esa venta.
export default function ProductSerialsManager({ productId }) {
  const { enqueueSnackbar } = useSnackbar();
  const [serials, setSerials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState('');
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    productsApi
      .getProductSerials(productId)
      .then(({ data }) => {
        if (active) setSerials(data.data);
      })
      .catch((error) => {
        if (active) enqueueSnackbar(getErrorMessage(error, 'No se pudieron cargar las series.'), { variant: 'error' });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const handleAdd = async () => {
    const serie = value.trim();
    if (!serie) {
      return;
    }
    setAdding(true);
    try {
      const { data } = await productsApi.addProductSerial(productId, serie);
      setSerials((prev) => [data.data, ...prev]);
      setValue('');
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo agregar la serie.'), { variant: 'error' });
    } finally {
      setAdding(false);
      inputRef.current?.focus();
    }
  };

  const handleDelete = async (serialId) => {
    setBusyId(serialId);
    try {
      await productsApi.deleteProductSerial(productId, serialId);
      setSerials((prev) => prev.filter((serial) => serial.id !== serialId));
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar la serie.'), { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const inStockCount = serials.filter((serial) => serial.status === 'in_stock').length;

  return (
    <Stack spacing={1}>
      <Stack sx={{ alignItems: 'flex-start' }} direction="row" spacing={1}>
        <TextField
          size="small"
          label="Número de serie"
          inputRef={inputRef}
          fullWidth
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          // readOnly (no disabled) mientras se guarda: un input deshabilitado pierde el foco y
          // la siguiente lectura de la pistola escaner se perderia.
          slotProps={{ htmlInput: { readOnly: adding } }}
        />
        <IconButton color="primary" onClick={handleAdd} disabled={adding || !value.trim()} title="Agregar serie">
          <AddIcon />
        </IconButton>
      </Stack>

      {loading ? (
        <Skeleton variant="rounded" height={32} width="60%" />
      ) : (
        <>
          {serials.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Aún no hay unidades con serie registrada para este producto.
            </Typography>
          ) : (
            <Stack sx={{ flexWrap: 'wrap', gap: 1 }} direction="row">
              {serials.map((serial) => (
                <Chip
                  key={serial.id}
                  label={serial.serie}
                  size="small"
                  color={serial.status === 'sold' ? 'success' : 'default'}
                  variant={serial.status === 'sold' ? 'filled' : 'outlined'}
                  title={serial.status === 'sold' ? 'Vendida' : 'En stock'}
                  disabled={busyId === serial.id}
                  onDelete={serial.status === 'in_stock' ? () => handleDelete(serial.id) : undefined}
                />
              ))}
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary">
            {inStockCount} serie(s) disponible(s). Se asignan solas a la venta (la más antigua
            primero); las vendidas quedan marcadas como registro y no se pueden eliminar.
          </Typography>
        </>
      )}
    </Stack>
  );
}

ProductSerialsManager.propTypes = {
  productId: PropTypes.number.isRequired,
};
